import { useState, useEffect, useMemo } from 'react'
import { format, subDays } from 'date-fns'
import {
  Database,
  Eye,
  Download,
  Copy,
  Search,
  Users,
  TrendingUp,
  TrendingDown,
  FileText,
  Image,
  Table2,
  Mail,
  Presentation,
  RefreshCw,
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  AlertCircle,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  contentHubAnalyticsService,
  type ContentAnalyticsOverviewDto,
} from '@/lib/api/services/contentHubAnalytics'

type DateRange = '7d' | '30d' | '90d' | '365d'

const dateRangeOptions: { value: DateRange; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '365d', label: 'Last year' },
]

const contentTypeIcons: Record<string, React.ElementType> = {
  TextElement: FileText,
  DataPoint: Database,
  ImageElement: Image,
  TableElement: Table2,
  EmailSnippet: Mail,
  SlideElement: Presentation,
}

const contentTypeColors: Record<string, string> = {
  TextElement: 'bg-blue-500',
  DataPoint: 'bg-purple-500',
  ImageElement: 'bg-green-500',
  TableElement: 'bg-amber-500',
  EmailSnippet: 'bg-pink-500',
  SlideElement: 'bg-cyan-500',
}

export default function ContentHubAnalytics() {
  const [dateRange, setDateRange] = useState<DateRange>('30d')
  const [loading, setLoading] = useState(true)
  const [overview, setOverview] = useState<ContentAnalyticsOverviewDto | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { startDate, endDate } = useMemo(() => {
    const end = new Date()
    const days = parseInt(dateRange)
    const start = subDays(end, days)
    return { startDate: start, endDate: end }
  }, [dateRange])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await contentHubAnalyticsService.getOverview(startDate, endDate)
      setOverview(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
      // Set mock data on error for development
      setOverview({
        stats: {
          totalItems: 0,
          textElements: 0,
          dataPoints: 0,
          imageElements: 0,
          tableElements: 0,
          emailSnippets: 0,
          slideElements: 0,
          totalViews: 0,
          totalInserts: 0,
          totalDownloads: 0,
          totalSearches: 0,
          uniqueUsers: 0,
          viewsChangePercent: 0,
          insertsChangePercent: 0,
        },
        topItems: [],
        leastUsedItems: [],
        usageTrend: [],
        usageByType: [],
        topUsers: [],
        popularSearches: [],
        noResultSearches: [],
        freshness: {
          updatedLast7Days: 0,
          updatedLast30Days: 0,
          updatedLast90Days: 0,
          updatedOver90Days: 0,
          neverUsed: 0,
          staleItems: [],
        },
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [startDate, endDate])

  const handleExport = async () => {
    try {
      const blob = await contentHubAnalyticsService.exportAnalytics(startDate, endDate)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `content-hub-analytics-${format(startDate, 'yyyyMMdd')}-${format(endDate, 'yyyyMMdd')}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Content Hub Analytics</h1>
          <p className="text-sm text-neutral-500">
            Track usage and performance of your content library
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={(v) => setDateRange(v as DateRange)}>
            <SelectTrigger className="w-40">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dateRangeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={loadData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 p-4 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">Unable to load real data: {error}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Content Items"
          value={overview?.stats.totalItems ?? 0}
          icon={Database}
          description={`${overview?.stats.textElements ?? 0} text, ${overview?.stats.dataPoints ?? 0} data points`}
        />
        <StatCard
          title="Total Views"
          value={overview?.stats.totalViews ?? 0}
          icon={Eye}
          change={overview?.stats.viewsChangePercent ?? 0}
        />
        <StatCard
          title="Total Inserts"
          value={overview?.stats.totalInserts ?? 0}
          icon={Copy}
          change={overview?.stats.insertsChangePercent ?? 0}
        />
        <StatCard
          title="Unique Users"
          value={overview?.stats.uniqueUsers ?? 0}
          icon={Users}
          description={`${overview?.stats.totalSearches ?? 0} searches`}
        />
      </div>

      {/* Main Content */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="content">Top Content</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="search">Search</TabsTrigger>
          <TabsTrigger value="freshness">Freshness</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Usage by Type */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-4 w-4" />
                  Usage by Content Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {overview?.usageByType && overview.usageByType.length > 0 ? (
                    overview.usageByType.map((type) => {
                      const Icon = contentTypeIcons[type.contentType] || FileText
                      return (
                        <div key={type.contentType} className="flex items-center gap-3">
                          <div
                            className={cn(
                              'flex h-8 w-8 items-center justify-center rounded-lg',
                              contentTypeColors[type.contentType] || 'bg-neutral-500'
                            )}
                          >
                            <Icon className="h-4 w-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-sm">
                              <span>{type.contentType.replace(/Element|Snippet/g, '')}</span>
                              <span className="font-medium">{type.usageCount}</span>
                            </div>
                            <Progress value={type.percentage} className="mt-1 h-1.5" />
                          </div>
                          <span className="text-xs text-neutral-500">{type.percentage}%</span>
                        </div>
                      )
                    })
                  ) : (
                    <p className="text-center text-sm text-neutral-500">No usage data available</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Usage Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Usage Trend
                </CardTitle>
                <CardDescription>Daily activity over the selected period</CardDescription>
              </CardHeader>
              <CardContent>
                {overview?.usageTrend && overview.usageTrend.length > 0 ? (
                  <div className="space-y-2">
                    {overview.usageTrend.slice(-7).map((point) => (
                      <div key={point.date} className="flex items-center gap-2 text-sm">
                        <span className="w-20 text-neutral-500">
                          {format(new Date(point.date), 'MMM d')}
                        </span>
                        <div className="flex-1">
                          <div className="flex h-4 gap-0.5 rounded-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                            <div
                              className="bg-blue-500"
                              style={{
                                width: `${(point.views / Math.max(point.views + point.inserts + point.downloads, 1)) * 100}%`,
                              }}
                            />
                            <div
                              className="bg-green-500"
                              style={{
                                width: `${(point.inserts / Math.max(point.views + point.inserts + point.downloads, 1)) * 100}%`,
                              }}
                            />
                            <div
                              className="bg-purple-500"
                              style={{
                                width: `${(point.downloads / Math.max(point.views + point.inserts + point.downloads, 1)) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                        <span className="w-12 text-right font-medium">
                          {point.views + point.inserts + point.downloads}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center gap-4 pt-2 text-xs">
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-2 rounded-full bg-blue-500" />
                        Views
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-2 rounded-full bg-green-500" />
                        Inserts
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-2 rounded-full bg-purple-500" />
                        Downloads
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-sm text-neutral-500">No trend data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="content" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Top Content */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  Most Used Content
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Usage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overview?.topItems && overview.topItems.length > 0 ? (
                      overview.topItems.map((item) => {
                        const Icon = contentTypeIcons[item.contentType] || FileText
                        return (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Icon className="h-3.5 w-3.5 text-neutral-500" />
                                <span className="text-xs text-neutral-500">
                                  {item.contentType.replace(/Element|Snippet/g, '')}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{item.totalUsage}</TableCell>
                          </TableRow>
                        )
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-neutral-500">
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Least Used */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-amber-500" />
                  Least Used Content
                </CardTitle>
                <CardDescription>Candidates for review or cleanup</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Usage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overview?.leastUsedItems && overview.leastUsedItems.length > 0 ? (
                      overview.leastUsedItems.map((item) => {
                        const Icon = contentTypeIcons[item.contentType] || FileText
                        return (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Icon className="h-3.5 w-3.5 text-neutral-500" />
                                <span className="text-xs text-neutral-500">
                                  {item.contentType.replace(/Element|Snippet/g, '')}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {item.totalUsage === 0 ? (
                                <Badge variant="outline" className="text-amber-600">
                                  Never used
                                </Badge>
                              ) : (
                                item.totalUsage
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-neutral-500">
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Top Contributors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Views</TableHead>
                    <TableHead className="text-right">Inserts</TableHead>
                    <TableHead className="text-right">Created</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overview?.topUsers && overview.topUsers.length > 0 ? (
                    overview.topUsers.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell className="font-medium">{user.userName}</TableCell>
                        <TableCell className="text-right">{user.viewCount}</TableCell>
                        <TableCell className="text-right">{user.insertCount}</TableCell>
                        <TableCell className="text-right">{user.createCount}</TableCell>
                        <TableCell className="text-right font-medium">{user.totalActions}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-neutral-500">
                        No user data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="search" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Popular Searches
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overview?.popularSearches && overview.popularSearches.length > 0 ? (
                    overview.popularSearches.map((search, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded-lg bg-neutral-50 px-3 py-2 dark:bg-neutral-800/50"
                      >
                        <span className="font-medium">"{search.query}"</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{search.searchCount} searches</Badge>
                          <span className="text-xs text-neutral-500">
                            avg {search.avgResultCount} results
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-sm text-neutral-500">No search data available</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  No Result Searches
                </CardTitle>
                <CardDescription>Content gaps to address</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overview?.noResultSearches && overview.noResultSearches.length > 0 ? (
                    overview.noResultSearches.map((search, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-900/20"
                      >
                        <span className="font-medium">"{search.query}"</span>
                        <Badge variant="outline" className="text-amber-600">
                          {search.searchCount} attempts
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-sm text-neutral-500">No failed searches</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="freshness" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Content Freshness
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <FreshnessBar
                    label="Updated last 7 days"
                    count={overview?.freshness.updatedLast7Days ?? 0}
                    total={overview?.stats.totalItems ?? 1}
                    color="bg-green-500"
                  />
                  <FreshnessBar
                    label="Updated 7-30 days ago"
                    count={overview?.freshness.updatedLast30Days ?? 0}
                    total={overview?.stats.totalItems ?? 1}
                    color="bg-blue-500"
                  />
                  <FreshnessBar
                    label="Updated 30-90 days ago"
                    count={overview?.freshness.updatedLast90Days ?? 0}
                    total={overview?.stats.totalItems ?? 1}
                    color="bg-amber-500"
                  />
                  <FreshnessBar
                    label="Over 90 days old"
                    count={overview?.freshness.updatedOver90Days ?? 0}
                    total={overview?.stats.totalItems ?? 1}
                    color="bg-red-500"
                  />
                  <FreshnessBar
                    label="Never used"
                    count={overview?.freshness.neverUsed ?? 0}
                    total={overview?.stats.totalItems ?? 1}
                    color="bg-neutral-400"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  Stale Content
                </CardTitle>
                <CardDescription>Content not used in over 90 days</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Days Stale</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overview?.freshness.staleItems && overview.freshness.staleItems.length > 0 ? (
                      overview.freshness.staleItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell className="text-xs text-neutral-500">
                            {item.contentType.replace(/Element|Snippet/g, '')}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline" className="text-amber-600">
                              {item.daysSinceLastUse}d
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-neutral-500">
                          No stale content
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ============================================
// Helper Components
// ============================================

interface StatCardProps {
  title: string
  value: number
  icon: React.ElementType
  description?: string
  change?: number
}

function StatCard({ title, value, icon: Icon, description, change }: StatCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-neutral-500">{title}</p>
            <p className="mt-1 text-2xl font-semibold">{value.toLocaleString()}</p>
            {description && <p className="mt-1 text-xs text-neutral-500">{description}</p>}
            {change !== undefined && (
              <div
                className={cn(
                  'mt-1 flex items-center gap-1 text-xs',
                  change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-neutral-500'
                )}
              >
                {change > 0 ? (
                  <TrendingUp className="h-3 w-3" />
                ) : change < 0 ? (
                  <TrendingDown className="h-3 w-3" />
                ) : null}
                <span>{change > 0 ? '+' : ''}{change.toFixed(1)}% from previous period</span>
              </div>
            )}
          </div>
          <div className="rounded-lg bg-neutral-100 p-3 dark:bg-neutral-800">
            <Icon className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface FreshnessBarProps {
  label: string
  count: number
  total: number
  color: string
}

function FreshnessBar({ label, count, total, color }: FreshnessBarProps) {
  const percentage = total > 0 ? (count / total) * 100 : 0
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span>{label}</span>
        <span className="font-medium">{count}</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-neutral-100 dark:bg-neutral-800">
        <div
          className={cn('h-full rounded-full transition-all', color)}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}
